import React, { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Stack } from '@atlaskit/primitives/compiled';
import { Box, xcss } from '@atlaskit/primitives';

import type {
	ITFGAnalyticsCallbackParams,
	Edition as InTrialFeatureGatesBannerEdition,
} from '@atlassian/in-trial-feature-gates-banner';
import {
	FEATURE_ID_USD_MODAL,
	formatEventCallBackPayloadForAtlasKitAnalyticsEvent,
} from '@atlassian/in-trial-feature-gates-banner';

import { useInTrialFeatureGatesExperimentEnrollmentAB } from '@confluence/in-trial-feature-gates-banner/entry-points/useInTrialFeatureGatesExperimentEnrollmentAB.ts';
import { InTrialFeatureGatesBannerWithData } from '@confluence/in-trial-feature-gates-banner/entry-points/InTrialFeatureGatesBannerWithData.ts';
import { NavigationLockout } from '@confluence/navigation-lockout';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { expValEquals } from '@confluence/feature-experiments';

import type { CtaNotifyMessageFooterProps } from '../footer/CtaNotifyMessageFooter.tsx';
import { CtaNotifyMessageFooter } from '../footer/CtaNotifyMessageFooter.tsx';

import { GeneralAccess, type GeneralAccessProps } from './generalAccess/GeneralAccess.tsx';
import { PrincipalsStager, type PrincipalsStagerProps } from './PrincipalsStager.tsx';
import { TitleAndButtons, type TitleAndButtonsProps } from './TitleAndButtons.tsx';
import { InfoMessage, type InfoMessageProps } from './infoMessage/InfoMessage.tsx';
import {
	SpecificAccessFacePileRow,
	type SpecificAccessFacePileRowProps,
} from './specificAccess/SpecificAccessFacePileRow.tsx';
import { SpecificAccessFilter, type SpecificAccessFilterProps } from './SpecificAccessFilter.tsx';
import { SpecificAccessTabs, type SpecificAccessTabsProps } from './SpecificAccessTabs.tsx';

export type HeaderProps = {
	titleAndButtons: TitleAndButtonsProps;
	generalAccess?: GeneralAccessProps;
	principalsStager?: PrincipalsStagerProps;
	infoMessage?: InfoMessageProps;
	specificAccessFacePileRow?: SpecificAccessFacePileRowProps;
	hasNavigationLockout: boolean;
	onSuggestedUserClick?: () => void;
	ctaNotifyMessageFooter?: CtaNotifyMessageFooterProps;
	specificAccessFilter?: SpecificAccessFilterProps;
	specificAccessTabs?: SpecificAccessTabsProps;
	// showDraftMessage: boolean; // TODO
};

const psaAdjustments = xcss({
	marginBottom: 'space.200',
});

export const Header = ({
	titleAndButtons,
	generalAccess,
	specificAccessFacePileRow,
	principalsStager,
	infoMessage,
	hasNavigationLockout,
	onSuggestedUserClick,
	ctaNotifyMessageFooter,
	specificAccessFilter,
	specificAccessTabs,
	// showDraftMessage,
}: HeaderProps) => {
	const { cloudId, edition } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const inTrialFeatureGatesAnalyticsCallback = useCallback(
		(params: ITFGAnalyticsCallbackParams) => {
			createAnalyticsEvent(formatEventCallBackPayloadForAtlasKitAnalyticsEvent(params)).fire();
		},
		[createAnalyticsEvent],
	);
	// AB hooks for in trial feature gates experiment - https://hello.atlassian.net/wiki/spaces/Land/pages/4343595130/Project+poster+Confluence+Feature+Gate+Add+Payment+Prompts
	const { canRenderTreatment } = useInTrialFeatureGatesExperimentEnrollmentAB({
		featureId: FEATURE_ID_USD_MODAL,
		tenantId: cloudId,
		sendOperationalEvent: inTrialFeatureGatesAnalyticsCallback,
		sendTrackEvent: inTrialFeatureGatesAnalyticsCallback,
		skip: edition === ConfluenceEdition.FREE,
		extraAnalyticsAttributes: {
			edition: edition?.toLowerCase(),
		},
	});

	const getSpacing = () => {
		if (expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')) {
			if (ctaNotifyMessageFooter?.natureOfCta === 'none') {
				return 'space.200';
			}
			return 'space.0';
		}
		return 'space.200';
	};

	return (
		<Stack grow="fill" space={getSpacing()}>
			<Box
				xcss={
					ctaNotifyMessageFooter && ctaNotifyMessageFooter.natureOfCta !== 'none' && psaAdjustments
				}
			>
				<TitleAndButtons {...titleAndButtons} />
			</Box>
			{principalsStager && (
				<PrincipalsStager {...principalsStager} onSuggestedUserClick={onSuggestedUserClick} />
			)}
			{specificAccessTabs && <SpecificAccessTabs {...specificAccessTabs} />}
			{specificAccessFilter && <SpecificAccessFilter {...specificAccessFilter} />}
			{infoMessage && <InfoMessage {...infoMessage} />}
			{canRenderTreatment && edition && (
				<InTrialFeatureGatesBannerWithData
					featureId={FEATURE_ID_USD_MODAL}
					edition={edition.toLowerCase() as InTrialFeatureGatesBannerEdition}
					tenantId={cloudId}
					sendOperationalEvent={inTrialFeatureGatesAnalyticsCallback}
					sendTrackEvent={inTrialFeatureGatesAnalyticsCallback}
					sendScreenEvent={inTrialFeatureGatesAnalyticsCallback}
				/>
			)}
			{ctaNotifyMessageFooter && <CtaNotifyMessageFooter {...ctaNotifyMessageFooter} />}
			{generalAccess && (
				<GeneralAccess {...generalAccess} showBottomPadding={!Boolean(specificAccessFacePileRow)} />
			)}
			{specificAccessFacePileRow && <SpecificAccessFacePileRow {...specificAccessFacePileRow} />}
			{hasNavigationLockout && <NavigationLockout />}
		</Stack>
	);
};
